/**
 * Server-recorded invocation failures for a builder session.
 *
 * Distinct from the Log tab next to it: the Log tab is a live capture of what
 * this browser saw, so it empties on reload and misses anything that failed
 * while the tab was closed. These rows come from the invocation gateway's own
 * audit trail, which means they persist and they carry the gateway's error
 * classification — the specific reason a call failed, not just that it did.
 */

import { useQuery } from '@tanstack/react-query';
import { AlertCircle, RefreshCw } from 'lucide-react';
import api from '@/lib/api';
import type { InvocationFailureDetail } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

interface InvocationFailuresContentProps {
  sessionId: string;
}

/**
 * Copy for the gateway's `EntityErrorType` values. These are the operator-side
 * classifications stored on the audit row — the developer-facing
 * `PluginInvocationErrorCode` (docs/plugin-error-codes.md) is what the plugin
 * receives at call time. Keeping the mapping here rather than widening the API
 * response means the wire stays a faithful echo of what was recorded.
 */
const ERROR_COPY: Record<string, { label: string; hint: string }> = {
  timeout: { label: 'Timed out', hint: 'The model did not respond in time. Not charged.' },
  provider_server: {
    label: 'Provider overloaded',
    hint: 'Upstream was over capacity. Retrying usually works.',
  },
  rate_limit: {
    label: 'Rate limited',
    hint: 'Upstream throttled the request. Back off and retry.',
  },
  quota: {
    label: 'Provider quota',
    hint: "The platform's provider account is exhausted. Report this.",
  },
  auth: {
    label: 'Provider auth',
    hint: "The platform's provider credentials failed. Report this.",
  },
  bad_request: {
    label: 'Rejected request',
    hint: 'Check prompt length, model choice, and image inputs.',
  },
  validation: {
    label: 'Invalid request',
    hint: 'Check prompt length, model choice, and image inputs.',
  },
  provider: { label: 'Provider error', hint: 'An unclassified upstream failure.' },
  internal: {
    label: 'Platform error',
    hint: 'A Fias-side failure. Report it with the request id.',
  },
  unknown: { label: 'Unknown error', hint: 'No classification was recorded.' },
  // Not an `EntityErrorType`. The server substitutes this for a row still
  // `pending` past the provider ceiling: `finalizeInvocationAudit` is
  // fire-and-forget, so a crash leaves the row unfinalized forever. Without an
  // entry here the crash case this whole tab exists to surface rendered as a
  // bare lowercase `interrupted` with no explanation.
  interrupted: {
    label: 'Interrupted',
    hint: 'The call never reported a result — usually a server restart mid-request.',
  },
};

function describe(errorCode: string | null): { label: string; hint: string } {
  if (!errorCode) return ERROR_COPY.unknown;
  return ERROR_COPY[errorCode] ?? { label: errorCode, hint: '' };
}

function formatTime(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
}

function FailureRow({ failure }: { failure: InvocationFailureDetail }) {
  const { label, hint } = describe(failure.errorCode);
  return (
    <li className="border-b last:border-b-0 px-3 py-2 text-xs">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-medium text-red-600 dark:text-red-400">{label}</span>
            {failure.outcome === 'cancelled' && (
              <span className="text-[10px] px-1 rounded bg-muted text-muted-foreground">
                cancelled
              </span>
            )}
          </div>
          {hint && <p className="text-muted-foreground mt-0.5">{hint}</p>}
          <p className="text-muted-foreground mt-1 font-mono truncate">{failure.entityId}</p>
        </div>
        <div className="text-right shrink-0 text-muted-foreground">
          <div>{formatTime(failure.startedAt)}</div>
          {failure.latencyMs != null && <div>{failure.latencyMs} ms</div>}
          {/* The correlation id support asks for. */}
          <div className="font-mono text-[10px] mt-1">{failure.requestId}</div>
        </div>
      </div>
    </li>
  );
}

export function InvocationFailuresContent({ sessionId }: InvocationFailuresContentProps) {
  const { data, isPending, isError, refetch, isFetching } = useQuery({
    queryKey: queryKeys.pluginBuilder.invocationFailures(sessionId),
    queryFn: () => api.getBuilderInvocationFailures(sessionId, { sinceHours: 24 * 7, limit: 50 }),
  });

  const failures = data?.failures ?? [];

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center justify-between border-b px-3 py-1.5 flex-shrink-0">
        <span className="text-xs text-muted-foreground">
          Failed AI calls from this session, last 7 days
        </span>
        <Button variant="ghost" size="sm" onClick={() => refetch()} disabled={isFetching}>
          <RefreshCw className={cn('h-3.5 w-3.5', isFetching && 'animate-spin')} />
        </Button>
      </div>

      <div className="flex-1 overflow-y-auto">
        {isPending ? (
          <p className="text-xs text-muted-foreground p-3">Loading…</p>
        ) : isError ? (
          <p className="text-xs text-muted-foreground p-3">
            Could not load invocation failures. Try again.
          </p>
        ) : failures.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-muted-foreground gap-2 p-6">
            <AlertCircle className="h-5 w-5" />
            <p className="text-xs text-center">
              No failed AI calls recorded. Failures that never reach the AI — a missing permission,
              an unknown entity id — are rejected before the gateway records anything, so they never
              appear here. Reproduce one with the Log tab open to see it.
            </p>
          </div>
        ) : (
          <ul>
            {failures.map((failure) => (
              <FailureRow key={failure.invocationId} failure={failure} />
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
